/**
 * Mall Map — 3D digital twin, wayfinding, and leasing intelligence.
 *
 * A standalone V3 page: it does not touch the DigitalTwin feature and does not
 * call the API. All figures come from data/mallData.js, which generates a
 * self-consistent demonstration estate from a seeded PRNG.
 *
 * Layout: a full-bleed WebGL canvas with floating chrome — toolbar on top,
 * floor selector and legend on the left, unit drawer on the right, and the
 * management dashboard as a bottom sheet.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Info, Maximize2, Minimize2, Upload, Database } from 'lucide-react';

import MallCanvas from './Map3D/MallCanvas';
import MapToolbar, { DEFAULT_FILTERS, applyFilters } from './Toolbar/MapToolbar';
import { FloorSwitcher, Legend } from './Toolbar/FloorSwitcher';
import UnitDetailPanel from './Panels/UnitDetailPanel';
import { CompareModal, LeaseDocModal } from './Panels/Modals';
import DirectionsPanel from './Panels/DirectionsPanel';
import OpsDashboard from './Panels/OpsDashboard';

import { UNITS, FLOORS, formatDateLong, buildCommercials } from './data/mallData';
import { loadPlan } from './Importer/importedPlan';
import { unpackBits } from './Importer/regionKinds';
import { buildPlanRouter } from './Importer/planRouting';
import SAMPLE_ROUTER from './data/wayfinding';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './mall-map.css';

function parseCssColor(str) {
    if (!str) return null;
    const s = str.trim();
    const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
        const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
        return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    }
    const rgb = s.match(/rgba?\(([^)]+)\)/i);
    if (rgb) {
        const p = rgb[1].split(/[,/\s]+/).filter(Boolean).map(Number);
        if (p.length >= 3 && p.slice(0, 3).every((n) => !isNaN(n))) return p.slice(0, 3);
    }
    return null;
}

/**
 * Which palette the WebGL scene should use, derived from the LIVE VALUE of the
 * `--v3-bg-base` token rather than from the `data-theme` attribute.
 *
 * Reading the attribute looks simpler and is wrong twice over. ThemePicker
 * stamps `data-theme` from its own mount effect, which can land after this
 * component's first render — and the premium skins (`body.v3-premium` +
 * `data-pm-theme`) repaint the tokens without touching `data-theme` at all.
 * Either way the canvas ends up dark inside light chrome. The computed token is
 * the same thing the surrounding CSS actually paints with, so it cannot drift.
 */
function readEffectiveTheme() {
    const target = document.body || document.documentElement;
    const raw = getComputedStyle(target).getPropertyValue('--v3-bg-base');
    const rgb = parseCssColor(raw);
    if (!rgb) return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
    const luminance = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
    return luminance > 140 ? 'light' : 'dark';
}

function useAppTheme() {
    const [theme, setTheme] = useState(readEffectiveTheme);
    useEffect(() => {
        const sync = () => setTheme(readEffectiveTheme());
        sync();   // catch anything stamped between first render and this effect
        const obs = new MutationObserver(sync);
        const opts = { attributes: true, attributeFilter: ['data-theme', 'data-skin', 'data-pm-theme', 'class', 'style'] };
        obs.observe(document.documentElement, opts);
        obs.observe(document.body, opts);
        window.addEventListener('v3-skin-change', sync);
        return () => {
            obs.disconnect();
            window.removeEventListener('v3-skin-change', sync);
        };
    }, []);
    return theme;
}

/** Flatten a unit into a one-row CSV report and hand it to the browser. */
function exportUnitReport(unit) {
    const l = unit.lease;
    const p = unit.performance;
    const o = unit.operations;

    const rows = [
        ['Unit code', unit.code],
        ['Trading name', unit.brand || ''],
        ['Category', unit.category],
        ['Floor', unit.floorName],
        ['Zone', unit.zoneName],
        ['Status', unit.status],
        ['GLA (sqm)', unit.gla],
        ['Vacancy risk', `${unit.riskLabel} (${unit.vacancyRisk})`],
        [],
        ['LEASE'],
        ['Start', l ? formatDateLong(l.startDate) : ''],
        ['End', l ? formatDateLong(l.endDate) : ''],
        ['Remaining (months)', l ? l.remainingMonths : ''],
        ['Type', l ? l.type : ''],
        ['Base rent (annual)', l ? l.baseRentAnnual : ''],
        ['Rent per sqm', l ? l.rentPerSqm : ''],
        ['Service charge (annual)', l ? l.serviceChargeAnnual : ''],
        ['Security deposit', l ? l.deposit : ''],
        ['Leasing manager', l ? l.manager.name : ''],
        [],
        ['PERFORMANCE'],
        ['Sales trailing 12m', p ? p.last12 : ''],
        ['Sales YTD', p ? p.ytd : ''],
        ['Sales per sqm', p ? p.salesPerSqm : ''],
        ['Footfall 30d', p ? p.footfall30d : ''],
        ['Conversion rate %', p ? p.conversionRate : ''],
        ['Average transaction value', p ? p.atv : ''],
        ['Occupancy cost ratio %', p ? p.occupancyCostRatio : ''],
        ['Category rank', p ? `${p.categoryRank} of ${p.categoryCount}` : ''],
        ['Mall rank', p ? `${p.mallRank} of ${p.mallCount}` : ''],
        ['Share of mall sales %', p ? p.pctOfMallSales : ''],
        [],
        ['MONTHLY SALES'],
        ...(p ? p.monthly.map((m) => [m.label, m.revenue]) : []),
        [],
        ['OPERATIONS'],
        ['Trading hours', o ? o.hours.label : ''],
        ['Phone', o ? o.phone : ''],
        ['Email', o ? o.email : ''],
        ['Staff on site', o ? o.staffCount : ''],
        ['Last fit-out', o ? formatDateLong(o.lastFitOut) : ''],
        ['Condition rating', o ? `${o.conditionRating} / 5` : ''],
    ];

    const csv = rows
        .map((r) => r.map((c) => {
            const s = String(c === undefined || c === null ? '' : c);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(','))
        .join('\r\n');

    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unit-report-${unit.code}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function MallMapPage() {
    const theme = useAppTheme();
    const canvasRef = useRef(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // An imported plan, if one has been published. Read once — it lives in
    // localStorage and only changes via the importer, which navigates here.
    const importedPlan = useMemo(() => loadPlan(), []);
    const wantImported = searchParams.get('source') === 'imported';
    const usingImported = !!importedPlan && wantImported;

    /**
     * Imported units get the same commercial model as the sample estate, but it
     * has to be applied HERE: what's persisted is geometry plus tenant identity,
     * not the derived lease/trading figures (they'd bloat storage and go stale).
     */
    const activeUnits = useMemo(() => {
        if (!usingImported) return UNITS;
        const list = importedPlan.floors.flatMap((f) => f.units || []);
        return buildCommercials(list.map((u) => ({ ...u })));
    }, [usingImported, importedPlan]);

    /** Plate size for an imported plan: the largest calibrated sheet in the set. */
    const planSize = useMemo(() => {
        if (!usingImported) return undefined;
        const w = Math.max(...importedPlan.floors.map((f) => f.widthM || 0), 60);
        const d = Math.max(...importedPlan.floors.map((f) => f.heightM || 0), 60);
        return { w: w * 1.12, d: d * 1.12 };   // a little apron around the plan
    }, [usingImported, importedPlan]);

    /**
     * Routing. The sample estate's graph is a module singleton; an imported
     * plan's is derived from its traced circulation the first time it is shown.
     *
     * buildPlanRouter also REWRITES each imported unit's `frontage` to the point
     * where its outline meets the mall, so the shopfront markers the scene draws
     * and the doors routes enter through are the same points. That has to happen
     * before the units reach the canvas, which is why it is in a memo the canvas
     * depends on rather than an effect.
     */
    const importedRouter = useMemo(
        () => (usingImported ? buildPlanRouter(importedPlan, activeUnits) : null),
        [usingImported, importedPlan, activeUnits],
    );
    const router = usingImported ? importedRouter : SAMPLE_ROUTER;

    /**
     * Non-leasable geometry for the scene. The sample estate models its floor
     * with declared courts and corridors, so it has none; an imported plan
     * carries whatever the operator classified as mall, atrium, core or apron.
     */
    const surfaces = useMemo(() => {
        if (!usingImported) return undefined;
        return importedPlan.floors.flatMap((f) => (f.surfaces || []).map((sf) => ({ ...sf, floor: f.id })));
    }, [usingImported, importedPlan]);

    /** Amenity pins for the scene: the sample set, or whatever was confirmed. */
    const amenities = useMemo(() => {
        if (!usingImported) return undefined;
        const out = [];
        importedPlan.floors.forEach((f) => {
            const widthM = f.widthM || 100;
            const heightM = f.heightM || 100;
            (f.amenities || []).forEach((a) => {
                out.push({
                    id: a.id,
                    type: a.type,
                    name: a.name,
                    x: (a.x - 0.5) * widthM,
                    z: (a.y - 0.5) * heightM,
                    floors: [f.id],
                });
            });
        });
        return out;
    }, [usingImported, importedPlan]);

    /**
     * The navigable model, per floor, unpacked for the scene.
     *
     * This is the same grid the router walks -- not a decorative overlay. Being
     * able to SEE it is what makes the routing auditable: a direction that comes
     * out wrong is nearly always a floor whose circulation was traced with a gap
     * in it, and that is invisible until you draw the grid over the plate.
     *
     * Unpacked here rather than in the scene so the renderer keeps no dependency
     * on the importer's bit packing.
     */
    const walkable = useMemo(() => {
        if (!usingImported) return undefined;
        const out = {};
        importedPlan.floors.forEach((f) => {
            const g = f.walkable;
            if (!g || !g.bits || !g.w || !g.h) return;
            out[f.id] = { w: g.w, h: g.h, cells: unpackBits(g.bits, g.w * g.h) };
        });
        return Object.keys(out).length ? out : undefined;
    }, [usingImported, importedPlan]);

    const unitById = useMemo(
        () => new Map(activeUnits.map((u) => [u.id, u])),
        [activeUnits],
    );
    const lookupUnit = useCallback((id) => unitById.get(id) || null, [unitById]);

    const [floor, setFloor] = useState('GF');
    const [selectedId, setSelectedId] = useState(null);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [heatmap, setHeatmap] = useState('none');
    const [stackMode, setStackMode] = useState(false);
    const [labels, setLabels] = useState(true);
    const [showWalkable, setShowWalkable] = useState(false);

    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [routeEnds, setRouteEnds] = useState({ fromId: null, toId: null });
    const [route, setRoute] = useState(null);

    const [dashboardOpen, setDashboardOpen] = useState(false);
    const [compareUnit, setCompareUnit] = useState(null);
    const [leaseUnit, setLeaseUnit] = useState(null);
    const [immersive, setImmersive] = useState(false);

    const selected = selectedId ? lookupUnit(selectedId) : null;

    // ── filtering ───────────────────────────────────────────────────────────
    const matched = useMemo(() => applyFilters(activeUnits, filters), [filters, activeUnits]);
    const matchedIds = useMemo(() => matched.map((u) => u.id), [matched]);

    // Passing null (rather than a full id list) tells the scene "no filter", which
    // skips the per-unit dimming pass entirely.
    const visibleUnitIds = useMemo(
        () => (matched.length === activeUnits.length ? null : matchedIds),
        [matched.length, matchedIds, activeUnits.length],
    );

    const counts = useMemo(() => {
        const out = {};
        FLOORS.forEach((f) => {
            const all = activeUnits.filter((u) => u.floor === f.id);
            out[f.id] = {
                total: all.length,
                visible: all.filter((u) => matchedIds.includes(u.id)).length,
                vacant: all.filter((u) => u.status === 'Vacant').length,
            };
        });
        return out;
    }, [matchedIds, activeUnits]);

    /*
     * Legend entries carry the colour the units are ACTUALLY drawn in, rather
     * than looking it up from the category again. An imported floor whose
     * shapes have not been categorised yet renders in the plan's own colours,
     * and a legend that answered "Services, grey" to a screen full of purple
     * and tan would simply be wrong.
     */
    const categoriesInView = useMemo(() => {
        const onFloor = activeUnits.filter((u) => u.floor === floor);
        const groups = new Map();
        onFloor.forEach((u) => {
            const asDrawn = !!u.planColor && !u.categorised;
            const key = asDrawn ? '\u0000plan' : u.category;
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    label: asDrawn ? 'As drawn on the plan' : u.category,
                    asDrawn,
                    colors: new Set(),
                });
            }
            groups.get(key).colors.add(u.color);
        });
        return [...groups.values()]
            .map((g) => ({ ...g, colors: [...g.colors] }))
            // Plan colours last: they are the shapes still waiting to be named.
            .sort((a, b) => (a.asDrawn === b.asDrawn
                ? a.label.localeCompare(b.label)
                : (a.asDrawn ? 1 : -1)));
    }, [floor, activeUnits]);

    // ── selection ───────────────────────────────────────────────────────────
    const selectUnit = useCallback((id, { fly = false } = {}) => {
        if (!id) { setSelectedId(null); return; }
        const unit = lookupUnit(id);
        if (!unit) return;
        setFloor(unit.floor);
        setSelectedId(id);
        if (fly) {
            // Let the floor swap settle before framing the unit.
            window.setTimeout(() => canvasRef.current && canvasRef.current.focusUnit(id), 120);
        }
    }, [lookupUnit]);

    // ── wayfinding ──────────────────────────────────────────────────────────
    const openDirectionsTo = useCallback((unit) => {
        const dest = router ? router.getEndpoint(`unit:${unit.id}`) : null;
        setRouteEnds((prev) => ({ fromId: prev.fromId, toId: dest ? dest.id : null }));
        setDirectionsOpen(true);
    }, [router]);

    const handleRoute = useCallback((r) => setRoute(r), []);

    useEffect(() => {
        if (!directionsOpen) setRoute(null);
    }, [directionsOpen]);

    // Endpoint ids belong to one estate's graph. Switching source has to reset
    // them, or the panel holds a start that no longer exists and silently
    // refuses to route.
    useEffect(() => {
        setRouteEnds({ fromId: null, toId: null });
        setRoute(null);
    }, [usingImported]);

    // Keep the visible floor in step with the leg of the route being walked.
    useEffect(() => {
        if (route && route.segments.length && route.segments[0].floor !== floor) {
            setFloor(route.segments[0].floor);
        }
    }, [route]);

    // ── keyboard ────────────────────────────────────────────────────────────
    useEffect(() => {
        const onKey = (e) => {
            const tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;
            if (e.key === 'Escape') {
                if (compareUnit) setCompareUnit(null);
                else if (leaseUnit) setLeaseUnit(null);
                else if (dashboardOpen) setDashboardOpen(false);
                else if (selectedId) setSelectedId(null);
                else if (directionsOpen) setDirectionsOpen(false);
                return;
            }
            const idx = ['1', '2', '3', '4'].indexOf(e.key);
            if (idx >= 0 && FLOORS[idx]) setFloor(FLOORS[idx].id);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [compareUnit, leaseUnit, dashboardOpen, selectedId, directionsOpen]);

    return (
        <div className={`mm-page${immersive ? ' is-immersive' : ''}${selected ? ' has-panel' : ''}${directionsOpen ? ' has-directions' : ''}`}>
            <MapToolbar
                floor={floor}
                filters={filters}
                onFilters={setFilters}
                onPickUnit={(u) => selectUnit(u.id, { fly: true })}
                heatmap={heatmap}
                onHeatmap={setHeatmap}
                stackMode={stackMode}
                onStackMode={setStackMode}
                labels={labels}
                onLabels={setLabels}
                walkableAvailable={!!walkable}
                showWalkable={showWalkable}
                onShowWalkable={setShowWalkable}
                directionsOpen={directionsOpen}
                onDirections={() => setDirectionsOpen((v) => !v)}
                dashboardOpen={dashboardOpen}
                onDashboard={() => setDashboardOpen((v) => !v)}
                onResetView={() => canvasRef.current && canvasRef.current.resetView()}
                resultCount={matched.length}
            />

            <div className="mm-stage">
                <MallCanvas
                    key={usingImported ? 'imported' : 'sample'}
                    ref={canvasRef}
                    units={activeUnits}
                    showFurniture={!usingImported}
                    planSize={planSize}
                    amenities={amenities}
                    surfaces={surfaces}
                    floor={floor}
                    selectedId={selectedId}
                    visibleUnitIds={visibleUnitIds}
                    heatmap={heatmap}
                    stackMode={stackMode}
                    labels={labels}
                    walkable={walkable}
                    showWalkable={showWalkable}
                    route={route}
                    theme={theme}
                    onSelect={(id) => selectUnit(id)}
                />

                <div className="mm-left">
                    <FloorSwitcher floor={floor} onFloor={setFloor} counts={counts} />
                    <Legend heatmap={heatmap} categoriesInView={categoriesInView} />
                </div>

                <div className="mm-source-bar">
                    {importedPlan && (
                        <div className="mm-source-toggle" role="group" aria-label="Data source">
                            <button
                                type="button"
                                title="Sample estate"
                                className={!usingImported ? 'is-active' : ''}
                                onClick={() => { searchParams.delete('source'); setSearchParams(searchParams); }}
                            >
                                <Database size={13} /> Sample estate
                            </button>
                            <button
                                type="button"
                                title={importedPlan.name || 'Imported plan'}
                                className={usingImported ? 'is-active' : ''}
                                onClick={() => setSearchParams({ source: 'imported' })}
                            >
                                <Upload size={13} /> {importedPlan.name || 'Imported plan'}
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className="mm-toggle"
                        onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/mallmap/import`)}
                        title="Import a floor plan"
                    >
                        <Upload size={14} /><span>Import plan</span>
                    </button>
                </div>

                <button
                    type="button"
                    className="mm-immersive-btn"
                    onClick={() => setImmersive((v) => !v)}
                    title={immersive ? 'Show chrome' : 'Hide chrome'}
                >
                    {immersive ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                </button>

                <div className="mm-hint">
                    <Info size={13} />
                    <span>Drag to orbit · scroll to zoom · right-drag to pan · keys 1–4 switch floors</span>
                </div>

                {directionsOpen && (
                    <DirectionsPanel
                        router={router}
                        fromId={routeEnds.fromId}
                        toId={routeEnds.toId}
                        onChange={setRouteEnds}
                        onClose={() => setDirectionsOpen(false)}
                        onRoute={handleRoute}
                    />
                )}

                {selected && (
                    <UnitDetailPanel
                        unit={selected}
                        theme={theme}
                        onClose={() => setSelectedId(null)}
                        onDirections={openDirectionsTo}
                        onCompare={setCompareUnit}
                        onViewLease={setLeaseUnit}
                        onExport={exportUnitReport}
                        onSelectUnit={(id) => selectUnit(id, { fly: true })}
                    />
                )}
            </div>

            {dashboardOpen && (
                <OpsDashboard
                    units={activeUnits}
                    theme={theme}
                    onClose={() => setDashboardOpen(false)}
                    onSelectUnit={(id) => { setDashboardOpen(false); selectUnit(id, { fly: true }); }}
                />
            )}

            {compareUnit && (
                <CompareModal
                    unit={compareUnit}
                    onClose={() => setCompareUnit(null)}
                    onSelectUnit={(id) => { setCompareUnit(null); selectUnit(id, { fly: true }); }}
                />
            )}

            {leaseUnit && <LeaseDocModal unit={leaseUnit} onClose={() => setLeaseUnit(null)} />}
        </div>
    );
}
